/**
 * SequenceActionsPopover - Lightweight action menu for Sequences (no preview).
 * Mirrors the asset preview popover placement and calm editorial styling.
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ViewTimelineOutlinedIcon from "@mui/icons-material/ViewTimelineOutlined";

export interface SequenceActionsPopoverProps {
    anchorEl: HTMLElement | null;
    sequence: { id: string; title?: string | null; name?: string | null } | null;
    onClose: () => void;
    onInsertIntoScript: (sequenceId: string) => void | Promise<void>;
    onEditSequence: (sequenceId: string) => void | Promise<void>;
}

export const SequenceActionsPopover: React.FC<SequenceActionsPopoverProps> = ({
    anchorEl,
    sequence,
    onClose,
    onInsertIntoScript,
    onEditSequence,
}) => {
    const open = Boolean(anchorEl && sequence?.id);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const WIDTH = 340;
    const GAP = 12;

    const computePosition = () => {
        if (!anchorEl) return;
        const rect = anchorEl.getBoundingClientRect();
        const h = containerRef.current?.offsetHeight ?? 160;
        let left = rect.left - GAP - WIDTH;
        let top = rect.top + rect.height / 2 - h / 2;
        const minLeft = 8;
        const minTop = 8;
        const maxTop = window.innerHeight - h - 8;
        if (left < minLeft) left = minLeft;
        if (top < minTop) top = minTop;
        if (top > maxTop) top = maxTop;
        setPosition({ top: Math.round(top), left: Math.round(left) });
    };

    useLayoutEffect(() => {
        if (!anchorEl) return;
        computePosition();
        const onScroll = () => computePosition();
        const onResize = () => computePosition();
        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);
        const id = window.setTimeout(computePosition, 0);
        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
            window.clearTimeout(id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorEl, sequence?.id]);

    if (!open || !anchorEl || !sequence?.id) return null;

    const title = String(sequence.title ?? sequence.name ?? "Sequence");

    return (
        <Box
            ref={containerRef}
            sx={{
                position: "fixed",
                top: position.top,
                left: position.left,
                width: WIDTH,
                borderRadius: "14px",
                boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.12)",
                bgcolor: "#fff",
                zIndex: (theme) => theme.zIndex.modal + 1,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "14px 16px",
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor: "rgba(124, 58, 237, 0.08)",
                        color: "#7C3AED",
                        flexShrink: 0,
                    }}
                >
                    <ViewTimelineOutlinedIcon sx={{ fontSize: 12 }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.02em" }}>
                        Sequence
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#111827",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                    title={title}
                >
                    {title}
                </Typography>

                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        ml: 0.5,
                        color: "#9CA3AF",
                        "&:hover": { backgroundColor: "#F3F4F6", color: "#374151" },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <Box sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                        <Button
                            data-tour="sequence-popover-insert-script"
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                void onInsertIntoScript(sequence.id);
                                onClose();
                            }}
                            sx={{
                                backgroundColor: "#111827",
                                color: "#fff",
                                textTransform: "none",
                                fontSize: 13,
                                fontWeight: 500,
                                padding: "10px 16px",
                                borderRadius: "10px",
                                boxShadow: "none",
                                "&:hover": {
                                    backgroundColor: "#1F2937",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            Insert into script
                        </Button>
                    </Box>

                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                                void onEditSequence(sequence.id);
                                onClose();
                            }}
                            sx={{
                                color: "#374151",
                                borderColor: "#E5E7EB",
                                textTransform: "none",
                                fontSize: 13,
                                fontWeight: 500,
                                padding: "10px 16px",
                                borderRadius: "10px",
                                "&:hover": {
                                    backgroundColor: "#F9FAFB",
                                    borderColor: "#D1D5DB",
                                },
                            }}
                        >
                            Edit sequence
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
